import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAppContext } from '../../context/AppContext';
import { queryApi } from '../../services/api';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const BulkQueryForm = ({ onResultsReceived }) => {
  const { topics, ragStrategies, selectedStrategy, setSelectedStrategy } = useAppContext();
  
  const [queries, setQueries] = useState([{ id: '1', question: '' }]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topK, setTopK] = useState(5);
  const [useReasoning, setUseReasoning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuery, setCurrentQuery] = useState('');

  const handleTopicsChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedTopics(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleStrategyChange = (event) => {
    setSelectedStrategy(event.target.value);
  };

  const handleTopKChange = (event, newValue) => {
    setTopK(newValue);
  };

  const handleAddQuery = () => {
    if (!currentQuery.trim()) {
      return;
    }
    
    const newId = (queries.length + 1).toString();
    setQueries([...queries, { id: newId, question: currentQuery.trim() }]);
    setCurrentQuery('');
  };

  const handleRemoveQuery = (id) => {
    setQueries(queries.filter(query => query.id !== id));
  };

  const handleQueryChange = (id, newQuestion) => {
    setQueries(queries.map(query => 
      query.id === id ? { ...query, question: newQuestion } : query
    ));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate queries
    const validQueries = queries.filter(q => q.question.trim());
    if (validQueries.length === 0) {
      setError('Please add at least one query.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const bulkQueryData = {
      queries: validQueries.map(q => ({
        id: q.id,
        question: q.question.trim(),
        topic_ids: selectedTopics.length > 0 ? selectedTopics : null,
      })),
      top_k: topK,
      rag_strategy: selectedStrategy,
      run_reasoning: useReasoning,
    };
    
    try {
      const response = await queryApi.bulkQuery(bulkQueryData);
      onResultsReceived(response);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to execute bulk query. Please try again.');
      onResultsReceived(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Bulk Query
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Queries
          </Typography>
          
          <List>
            {queries.map((query) => (
              <ListItem key={query.id} sx={{ pl: 0, pr: 0 }}>
                <TextField
                  fullWidth
                  value={query.question}
                  onChange={(e) => handleQueryChange(query.id, e.target.value)}
                  placeholder="Enter your question"
                  variant="outlined"
                  size="small"
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    aria-label="delete"
                    onClick={() => handleRemoveQuery(query.id)}
                    disabled={queries.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ display: 'flex', mt: 2 }}>
            <TextField
              fullWidth
              label="Add new query"
              value={currentQuery}
              onChange={(e) => setCurrentQuery(e.target.value)}
              placeholder="e.g., What are the risk factors for neonatal jaundice?"
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddQuery}
              disabled={!currentQuery.trim()}
              sx={{ ml: 1 }}
            >
              <AddIcon />
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel id="topics-select-label">Filter by Topics (Optional)</InputLabel>
            <Select
              labelId="topics-select-label"
              multiple
              value={selectedTopics}
              onChange={handleTopicsChange}
              input={<OutlinedInput label="Filter by Topics (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {topics.map((topic) => (
                <MenuItem key={topic} value={topic}>
                  {topic}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel id="rag-strategy-label">RAG Strategy</InputLabel>
            <Select
              labelId="rag-strategy-label"
              value={selectedStrategy}
              label="RAG Strategy"
              onChange={handleStrategyChange}
            >
              {ragStrategies.map((strategy) => (
                <MenuItem key={strategy.id} value={strategy.id}>
                  {strategy.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography id="top-k-slider" gutterBottom>
            Number of chunks to retrieve (top-k): {topK}
          </Typography>
          <Slider
            value={topK}
            onChange={handleTopKChange}
            aria-labelledby="top-k-slider"
            valueLabelDisplay="auto"
            step={1}
            marks
            min={1}
            max={10}
          />
        </Box>
        
        <FormControlLabel
          control={
            <Switch
              checked={useReasoning}
              onChange={(e) => setUseReasoning(e.target.checked)}
              color="primary"
            />
          }
          label="Use reasoning loop for better answers"
          sx={{ mb: 3 }}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 1 }}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
        >
          {loading ? 'Processing...' : 'Run Bulk Query'}
        </Button>
      </Box>
    </Paper>
  );
};

export default BulkQueryForm;
